import { useState, useEffect } from 'react';

export interface AudienceOverviewProps {
  sessionId: string;
  refreshInterval?: number;
  compact?: boolean;
}

interface TeamStats {
  teamColor: string;
  count: number;
  percentage: number;
}

interface AudienceStats {
  totalAudience: number;
  teams: TeamStats[];
  lastUpdated: number;
}

const TEAM_COLORS: Record<string, { bg: string; text: string }> = {
  BLUE: { bg: 'bg-blue-500', text: 'text-blue-300' },
  RED: { bg: 'bg-red-500', text: 'text-red-300' },
  GREEN: { bg: 'bg-green-500', text: 'text-green-300' },
  YELLOW: { bg: 'bg-yellow-500', text: 'text-yellow-300' },
};

const TEAM_NAMES: Record<string, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

export function AudienceOverview({ sessionId, refreshInterval = 3000, compact = false }: AudienceOverviewProps) {
  const [stats, setStats] = useState<AudienceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, refreshInterval);
    return () => clearInterval(interval);
  }, [sessionId, refreshInterval]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}/audience-stats`);

      if (!response.ok) {
        throw new Error('Failed to fetch audience stats');
      }

      const data = await response.json();
      setStats(data.stats);
      setError(null);
      setLoading(false);

      // Update last updated time
      const now = new Date();
      setLastUpdated(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`);
    } catch (err: any) {
      console.error('[AudienceOverview] Error fetching stats:', err);
      setError(err.message || 'Failed to load stats');
      setLoading(false);
    }
  };

  const handleResetAudience = async () => {
    if (!confirm('Sei sicuro di voler resettare tutti gli spettatori? Questa azione disconnetterà tutti gli utenti collegati.')) {
      return;
    }

    setResetting(true);
    try {
      const response = await fetch(`/api/sessions/${sessionId}/reset-audience`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to reset audience');
      }

      const data = await response.json();
      console.log(`[AudienceOverview] Reset ${data.totalReset} audience members`);

      // Refresh stats immediately
      fetchStats();
    } catch (err: any) {
      console.error('[AudienceOverview] Error resetting audience:', err);
      alert('Errore durante il reset degli spettatori');
    } finally {
      setResetting(false);
    }
  };

  // Compact mode: inline team counts
  if (compact) {
    if (loading) {
      return (
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-sm">👥</span>
          <div className="animate-pulse h-4 w-24 bg-gray-700 rounded"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-red-400 text-sm">👥 Errore</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-gray-400 text-sm font-semibold">👥</span>
        {stats && stats.teams.length > 0 ? (
          stats.teams.map((team) => {
            const colors = TEAM_COLORS[team.teamColor] || { bg: 'bg-gray-500', text: 'text-gray-300' };
            return (
              <div key={team.teamColor} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${colors.bg}`}></div>
                <span className={`text-sm font-semibold ${colors.text}`}>{team.count}</span>
              </div>
            );
          })
        ) : (
          <span className="text-gray-500 text-sm">0</span>
        )}
        <span className="text-white font-bold text-sm">= {stats?.totalAudience || 0}</span>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4">Panoramica Spettatori</h3>
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-gray-700 rounded"></div>
          <div className="h-12 bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4">Panoramica Spettatori</h3>
        <div className="text-red-400 text-sm">Errore: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Panoramica Spettatori</h3>
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-green-400 text-xs font-semibold">Live</span>
        </div>
      </div>

      {/* Team Stats */}
      <div className="space-y-3">
        {stats && stats.teams.length > 0 ? (
          stats.teams.map((team) => {
            const colors = TEAM_COLORS[team.teamColor] || { bg: 'bg-gray-500', text: 'text-gray-300' };
            return (
              <div key={team.teamColor} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${colors.bg}`}></div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-white font-semibold">Squadra {TEAM_NAMES[team.teamColor] || team.teamColor}</span>
                    <span className="text-gray-300">{team.count} spettatori</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${colors.bg} transition-all duration-300`}
                      style={{ width: `${team.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-gray-400 text-sm italic text-center py-4">
            Nessun spettatore ancora connesso
          </div>
        )}
      </div>

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <div className="flex justify-between items-center">
          <span className="text-gray-400">Totale Spettatori</span>
          <span className="text-white text-2xl font-bold">{stats?.totalAudience || 0}</span>
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-2 text-xs text-gray-500 text-center">
        Ultimo aggiornamento: {lastUpdated}
      </div>

      {/* Reset Button */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={handleResetAudience}
          disabled={resetting || (stats?.totalAudience || 0) === 0}
          className="w-full px-4 py-2 text-sm font-medium text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resetting ? 'Reset in corso...' : 'Reset Spettatori'}
        </button>
      </div>
    </div>
  );
}
